<template>
  <div class="template-view">
    <div class="template-topbar-container">
      <FriedGetAcceptLogo class="app-logo" />
    </div>
    <div ref="templateContainer" class="template-content-container">
      <div class="template-container">
        <div class="template">
          <div v-if="loading" class="circle">
            <FriedSpinner />
          </div>
          <template v-else-if="!!pages.length">
            <div class="document-header">
              <FriedH4>{{ document.name }}</FriedH4>
            </div>
            <Page
              v-for="page of pages"
              :key="page.id"
              :page="page"
              :pages="pages"
              :page-width="pageWidth"
            />
          </template>
        </div>
      </div>
      <SignupTemplateSidebar
        class="signup-template-sidebar hidden-sm-and-down"
        @signup="toggleSignup"
      />
      <EngageVideo
        v-if="showEngageVideo"
        :id="document.video.video.videoId"
        :url="document.video.video.videoUrl"
        @close="handleCloseEngageVideo"
      />
      <FriedModal v-if="showSignup" class="modal-backdrop" @close="toggleSignup">
        <Signup
          is-modal
          :template-id="templateId"
          title="welcome-to-getaccept-template"
          registration-source="proposal-template"
          registration-campaign-id="globaltemplate"
          button-text="sign-up-template"
        />
      </FriedModal>
    </div>
  </div>
</template>

<script lang="ts">
import type { Document } from '@getaccept/lib-shared-new/src/types/Document';
import EngageVideo from '@getaccept/lib-shared-new/src/video/player/components/EngageVideo.vue';
import { validateEmail } from '@getaccept/lib-shared-new/src/helpers';
import { TemplateService } from '@getaccept/editor-lib-new';
import type { Ref } from 'vue';
import { ref, computed, onMounted, onUnmounted, defineComponent } from 'vue';
import { LocalStorageHelper } from '@getaccept/lib-shared-new/src/helpers/local-storage.helper';
import { LocalStorageKey } from '@getaccept/lib-shared-new/src/enums/local-storage';
import Signup from '../../signup/components/Signup.vue';
import Page from './Page.vue';
import SignupTemplateSidebar from './SignupTemplateSidebar.vue';

export default defineComponent({
  name: 'SignupTemplateViewer',
  components: {
    Page,
    EngageVideo,
    SignupTemplateSidebar,
    Signup,
  },
  props: { templateId: { type: String, default: '' } },
  setup(props) {
    const showVideo = ref(true);
    const email = ref('');
    const showSignup = ref(false);
    const pageWidth = ref(1000);
    const document: Ref<Document> = ref(null);
    const loading = ref(false);
    const error = ref(false);

    const pages = computed(() => document.value?.pages || []);

    const video = computed(() => document.value?.video || null);
    const validEmail = computed(
      () => email.value.length > 0 && validateEmail(email.value.toLowerCase())
    );
    const showEngageVideo = computed(() => !!video.value && !!video.value.video && showVideo.value);

    const handleCloseEngageVideo = () => {
      showVideo.value = false;
    };

    const toggleSignup = () => {
      const isAuthenticated = localStorage.getItem('GA_SESSION_STATE.token');
      LocalStorageHelper.setLocalStorage(LocalStorageKey.Template, props.templateId);
      if (isAuthenticated) {
        window.location.href = '/dashboard';
      } else {
        showSignup.value = !showSignup.value;
      }
    };

    const setPageWidth = () => {
      pageWidth.value = window.innerWidth;
    };

    const loadTemplate = async () => {
      try {
        loading.value = true;
        document.value = await TemplateService.fetch(props.templateId);
      } catch (e) {
        error.value = true;
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      window.addEventListener('resize', setPageWidth);
      if (props.templateId) {
        loadTemplate();
      }
    });

    onUnmounted(() => {
      window.removeEventListener('resize', setPageWidth);
    });

    return {
      showVideo,
      email,
      showSignup,
      pageWidth,
      document,
      loading,
      error,
      pages,
      video,
      validEmail,
      handleCloseEngageVideo,
      toggleSignup,
      setPageWidth,
      loadTemplate,
      showEngageVideo,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '@getaccept/lib-shared-new/src/assets/scss/layout';

.template-view {
  background: url('../../assets/img/signing-page-bg.png');
  background-repeat: no-repeat;
  background-attachment: fixed;
  background-size: cover;
  min-height: 100%;
}

.template-topbar-container {
  position: sticky;
  top: 0;
  display: flex;
  padding: var(--spacing-100);
  background-color: var(--white);
  box-shadow: var(--shadow-1);
  z-index: $z1;
  height: 4.5rem;

  @media only screen and (max-width: $sm) {
    height: 4rem;
  }

  .app-logo {
    width: 6rem;
  }
}

.template-content-container {
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  @media only screen and (max-width: $sm) {
    flex-direction: column;
  }

  .template-container {
    display: flex;
    flex: 4;
    justify-content: center;
    margin-top: var(--spacing-150);

    @media only screen and (max-width: $sm) {
      margin: var(--spacing-100);
      flex: 0;
    }

    .circle {
      display: flex;
      background: white;
      border-radius: 100%;
      padding: var(--spacing-50);
      transform: translate(-50%, -50%);
      position: absolute;
      top: 50%;
      left: 50%;
    }

    .template {
      width: 70%;
      max-width: var(--page-width);
      margin: 0 auto 5rem;
      position: relative;
      min-height: 100vh;

      @media only screen and (max-width: $sm) {
        width: 100%;
      }

      :deep(.page) {
        margin-top: var(--spacing-50);
      }

      .document-view-page:not(:first-child) {
        margin: var(--spacing-100) 0;
      }
    }

    .document-header {
      background-color: var(--white);
      padding: var(--spacing-100) var(--spacing-150);
      box-shadow: var(--shadow-1);
      border-radius: var(--radius-medium);
      margin: 0 0 var(--spacing-75);
    }
  }

  .signup-template-sidebar {
    flex: 1;
  }

  .email {
    margin-top: var(--spacing-100);
    width: 100%;
  }

  .template-footer {
    display: flex;
    flex-direction: row-reverse;

    @media only screen and (max-width: $xs) {
      margin: var(--spacing-100);

      button {
        width: 100%;
      }
    }
  }
}

.template-footer-container {
  width: 100%;
  background-color: var(--white);
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 4rem;
  z-index: $z3;
  padding: var(--spacing-100);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: var(--box-shadow-3);

  button {
    margin: var(--spacing-50) 0;
    width: 100%;
  }
}
</style>
